import { AttendanceEnumsBreakViolationsHandlingEnum, Maybe } from 'generated/resources/graphql'

import { useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateAttendanceBreakSetting } from 'modules/attendance/hooks/useCreateAttendanceBreakSettings'
import { AUTOMATIC_BREAK_SOURCE } from 'modules/attendance/hooks/useFetchBreakSettings'
import { BreakSettingsType } from 'modules/attendance/hooks/useFetchBreakSettings'
import { useUpdateAttendanceBreakSetting } from 'modules/attendance/hooks/useUpdateAttendanceBreakSettings'

interface AutomaticBreakRuleAttr {
  __typename?: string
  id?: string | null
  periodLength?: number | null
  timeSettingsBreakConfigurationId?: string | null
  breakDuration?: number | null
  _destroy?: boolean
}

interface DurationRuleAttr {
  __typename?: string
  _destroy?: boolean
  id?: string | null
  minimalDuration?: number | null
  periodLength?: number | null
}

export interface BreakSettingsFormValues {
  id?: string | null
  manualBreaks: boolean
  automaticBreaks: boolean
  automaticBreakRulesAttributes: AutomaticBreakRuleAttr[]
  durationRulesAttributes: DurationRuleAttr[]
  restTime: Maybe<boolean> | undefined
  sendNotification: boolean
  baseMinimalDuration?: number | undefined | null
  attendanceEmployeesSettingId: string | undefined
  automaticBreakSource: string
  breakViolationsHandling?: AttendanceEnumsBreakViolationsHandlingEnum
}

const BreakSettingsForm = ({
  settingId,
  breakSettingId,
  breakSetting,
}: {
  settingId: string
  breakSettingId: string | undefined
  breakSetting: BreakSettingsType | undefined
}) => {
  const { create } = useCreateAttendanceBreakSetting()
  const { update } = useUpdateAttendanceBreakSetting()

  const defaultValues: BreakSettingsFormValues = {
    id: breakSettingId || breakSetting?.id,
    manualBreaks: !!breakSetting?.manualBreaks,
    automaticBreaks: !!breakSetting?.automaticBreaks,
    automaticBreakRulesAttributes: breakSetting?.attendanceAutomaticBreakRules || [],
    durationRulesAttributes: breakSetting?.attendanceBreakDurationRules || [],
    restTime: breakSetting?.restTime,
    sendNotification: false,
    baseMinimalDuration: breakSetting?.baseMinimalDuration,
    attendanceEmployeesSettingId: settingId,
    automaticBreakSource: breakSetting?.automaticBreakSource || AUTOMATIC_BREAK_SOURCE.RULES,
    breakViolationsHandling: breakSetting?.breakViolationsHandling || 'no_action',
  }

  const form = useForm({
    defaultValues: defaultValues,
    onSubmit: async (data, { setErrors }) => {
      const automaticBreakRulesAttributesPayload: AutomaticBreakRuleAttr[] =
        data.automaticBreakRulesAttributes.map(({ __typename, ...rest }) => ({
          ...rest,
          _destroy: rest._destroy || false,
        }))

      const durationRulesAttributesPayload: DurationRuleAttr[] = data.durationRulesAttributes.map(
        ({ __typename, ...rest }) => ({
          ...rest,
          _destroy: rest._destroy || false,
        })
      )

      const payload = {
        id: data.id,
        manualBreaks: data.manualBreaks,
        automaticBreaks: data.automaticBreaks,
        automaticBreakRulesAttributes: automaticBreakRulesAttributesPayload,
        baseMinimalDuration: data.baseMinimalDuration,
        durationRulesAttributes: durationRulesAttributesPayload,
        restTime: data.restTime,
        sendNotification: data.sendNotification,
        attendanceEmployeesSettingId: data.attendanceEmployeesSettingId,
        automaticBreakSource: data.automaticBreakSource,
        breakViolationsHandling: data.breakViolationsHandling,
      }

      let handled

      if (payload.id != null) {
        const result = await update({ ...payload, id: payload.id })
        handled = handleResult(result, setErrors)
      } else {
        const result = await create(payload)
        handled = handleResult(result, setErrors)
      }

      if (handled.success) {
        const resetFormData: BreakSettingsFormValues = {
          id: handled.data.breakSetting?.id,
          manualBreaks: !!handled.data.breakSetting?.manualBreaks,
          automaticBreaks: !!handled.data.breakSetting?.automaticBreaks,
          restTime: !!handled.data.breakSetting?.restTime,
          automaticBreakRulesAttributes:
            handled.data.breakSetting?.attendanceAutomaticBreakRules || [],
          durationRulesAttributes: handled.data.breakSetting?.attendanceBreakDurationRules || [],
          baseMinimalDuration: handled.data.breakSetting?.baseMinimalDuration,
          attendanceEmployeesSettingId: handled.data.breakSetting?.attendanceEmployeesSettingId,
          sendNotification: false,
          automaticBreakSource:
            handled.data.breakSetting?.automaticBreakSource || AUTOMATIC_BREAK_SOURCE.RULES,
          breakViolationsHandling:
            handled.data.breakSetting?.breakViolationsHandling || 'no_action',
        }

        form.resetForm({ values: resetFormData })

        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }

      return handled
    },
  })

  return form
}

export default BreakSettingsForm
